import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { ToastrService } from 'ngx-toastr';
import { ApiService } from '@services/api/api.service';
import { SalesAssistantService } from '@services/api/sales-assistant.service';
import { RemmiConfirmService } from '@common/remmi-confirm/remmi-confirm.service';

interface AgentOption {
  _id: string;
  full_name: string;
}

/**
 * Admin control for linking a Sales Assistant to the agent(s) they may act as
 * (SALES_ASSISTANT_FRONTEND.md §6). Rendered on the staff-edit screen.
 *
 * Visibility is decided by the PARENT (admin + the selected role has
 * `can_access_other_users`); this component just loads the office's agents,
 * pre-fills the existing link, and upserts on save.
 */
@Component({
  selector: 'app-assign-agents',
  standalone: true,
  imports: [CommonModule, FormsModule, NgSelectModule],
  templateUrl: './assign-agents.component.html',
  styleUrls: ['./assign-agents.component.scss'],
})
export class AssignAgentsComponent implements OnChanges {
  /** The assistant (edited staff member) to assign agents to. */
  @Input() assistantUserId!: string;
  /** Office scope for the link + the agent list. */
  @Input() officeId?: string;

  /** Selectable agents in this office (excludes the assistant themselves). */
  agentOptions: AgentOption[] = [];
  /** Currently selected agent ids (bound to the multi-select). */
  selectedAgentIds: string[] = [];

  loading = false;
  saving = false;

  constructor(
    private salesAssistant: SalesAssistantService,
    private api: ApiService,
    private toastr: ToastrService,
    private remmiConfirm: RemmiConfirmService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['assistantUserId'] && this.assistantUserId) {
      this.loadOfficeAgents();
      this.loadExistingLink();
    }
  }

  private loadOfficeAgents(): void {
    if (!this.officeId) {
      this.agentOptions = [];
      return;
    }
    this.loading = true;
    this.api.get(`/users/byoffice?office=${this.officeId}&show=staff`).subscribe({
      next: response => {
        this.loading = false;
        const staff: any[] = response?.data || [];
        this.agentOptions = staff
          .filter(user => user?.deactive !== true && user?._id !== this.assistantUserId)
          .map(user => ({
            _id: user._id,
            full_name: `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email || user._id,
          }))
          .sort((a, b) => a.full_name.localeCompare(b.full_name));
      },
      error: () => {
        this.loading = false;
        this.agentOptions = [];
      },
    });
  }

  private loadExistingLink(): void {
    this.salesAssistant.getLink(this.assistantUserId).subscribe({
      next: response => {
        const agents: any[] = response?.data?.agents || [];
        this.selectedAgentIds = agents.map(agent => agent?._id).filter(Boolean);
      },
      error: () => {
        // 404 (no link yet) or other error → start with an empty selection.
        this.selectedAgentIds = [];
      },
    });
  }

  save(): void {
    if (this.saving) {
      return;
    }
    this.saving = true;
    const payload: { assistant_user_id: string; agent_user_ids: string[]; office_id?: string } = {
      assistant_user_id: this.assistantUserId,
      agent_user_ids: this.selectedAgentIds || [],
    };
    if (this.officeId) {
      payload.office_id = this.officeId;
    }
    this.salesAssistant.upsertLink(payload).subscribe({
      next: () => {
        this.saving = false;
        this.toastr.success('Assigned agents updated');
      },
      error: err => {
        this.saving = false;
        this.toastr.error(err?.status === 403 ? 'You are not permitted to manage assistant links' : 'Could not update assigned agents');
      },
    });
  }

  /** Remove the assistant's link entirely. */
  clearAll(): void {
    if (this.saving || !this.selectedAgentIds.length) {
      this.selectedAgentIds = [];
      return;
    }
    const count = this.selectedAgentIds.length;
    this.remmiConfirm
      .delete({
        title: 'Remove All Agents?',
        message: 'This assistant will lose access to every assigned agent.',
        confirmLabel: 'Remove All',
        subject: `${count} ${count === 1 ? 'agent' : 'agents'} assigned`,
      })
      .subscribe(isConfirmed => {
        if (!isConfirmed) return;
        this.saving = true;
        this.salesAssistant.deleteLink(this.assistantUserId).subscribe({
          next: () => {
            this.saving = false;
            this.selectedAgentIds = [];
            this.toastr.success('Assistant link removed');
          },
          error: () => {
            this.saving = false;
            this.toastr.error('Could not remove assistant link');
          },
        });
      });
  }
}
